/**
 * Utility functions for handling sequence reordering in hierarchical training structures
 */

export interface SequenceItem {
  id: string;
  sequence: number;
}

/**
 * Move an item up in the sequence (decrease sequence number)
 */
export const moveUp = <T extends SequenceItem>(
  item: T,
  allItems: T[]
): { item: T; swapItem: T | null } | null => {
  const sortedItems = [...allItems].sort((a, b) => a.sequence - b.sequence);
  const currentIndex = sortedItems.findIndex((i) => i.id === item.id);

  if (currentIndex <= 0) {
    return null; // Already at the top
  }

  const currentItem = sortedItems[currentIndex];
  const previousItem = sortedItems[currentIndex - 1];
  return {
    item: { ...currentItem, sequence: previousItem.sequence },
    swapItem: { ...previousItem, sequence: currentItem.sequence },
  };
};

/**
 * Move an item down in the sequence (increase sequence number)
 */
export const moveDown = <T extends SequenceItem>(
  item: T,
  allItems: T[]
): { item: T; swapItem: T | null } | null => {
  const sortedItems = [...allItems].sort((a, b) => a.sequence - b.sequence);
  const currentIndex = sortedItems.findIndex((i) => i.id === item.id);

  if (currentIndex === -1 || currentIndex >= sortedItems.length - 1) {
    return null; // Already at the bottom
  }

  const currentItem = sortedItems[currentIndex];
  const nextItem = sortedItems[currentIndex + 1];
  return {
    item: { ...currentItem, sequence: nextItem.sequence },
    swapItem: { ...nextItem, sequence: currentItem.sequence },
  };
};

/**
 * Normalize sequences to consecutive numbers (1, 2, 3, ...)
 */
export const reorderSequences = <T extends SequenceItem>(items: T[]): T[] => {
  const sortedItems = [...items].sort((a, b) => a.sequence - b.sequence);
  return sortedItems.map((item, index) => ({
    ...item,
    sequence: index + 1,
  }));
};

/**
 * Get the next available sequence number
 */
export const getNextSequence = <T extends SequenceItem>(items: T[]): number => {
  if (items.length === 0) return 1;
  return Math.max(...items.map((item) => item.sequence)) + 1;
};

/**
 * Check if an item can be moved up
 */
export const canMoveUp = <T extends SequenceItem>(
  item: T,
  allItems: T[]
): boolean => {
  const sortedItems = [...allItems].sort((a, b) => a.sequence - b.sequence);
  const currentIndex = sortedItems.findIndex((i) => i.id === item.id);
  return currentIndex > 0;
};

/**
 * Check if an item can be moved down
 */
export const canMoveDown = <T extends SequenceItem>(
  item: T,
  allItems: T[]
): boolean => {
  const sortedItems = [...allItems].sort((a, b) => a.sequence - b.sequence);
  const currentIndex = sortedItems.findIndex((i) => i.id === item.id);
  return currentIndex !== -1 && currentIndex < sortedItems.length - 1;
};



